import { ChangeEvent, FC, Fragment, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
// Types
import Colors from 'app/types/Colors';
import { AuditOperations } from 'app/store/Audit/Audit.types';
// Models
import ILabel from 'app/models/Label';
import { IMyUser } from 'app/models/User';
import { RootState } from 'app/store';
// Redux
import { useAppDispatch, useAppSelector } from 'app/hooks/useStore';
// Async
import { patchInsuranceCase } from 'app/store/Cases/Cases.async';
import { createAuditRecord } from 'app/store/Audit/Audit.async';
// Selectors
import { selectMyUser } from 'app/store/Users/Users.selectors';
import { selectCaseForJsonPatch, selectLoading } from 'app/store/Cases/Cases.selectors';
import { selectPushAlert } from 'app/store/AppUINotifications/AppUINotifications.selectors';
// Mui
import { Box } from '@mui/material';
// Components
import Dialog from 'app/components/Dialog';
import ColorPalette from 'app/components/ColorPalette';
import Labels from 'app/components/Labels';
import CasePushAlert from 'app/components/CasePushAlert';
import { Input, Button } from 'app/components/Mui';

type Props = {
  open: boolean;
  onClose: () => void;
  insuranceCaseId: number;
  version: number;
  labels?: ILabel[];
};

const CaseLabelsDialog:FC<Props> = ({
  // Props
  open, onClose, insuranceCaseId, version, labels = []
}) => {
  const { t } = useTranslation();

  const auditLabelsRef = useRef<{
    added: ILabel[];
    removed: ILabel[];
  }>({ added: [], removed: [] });

  // Dispatch
  const dispatch = useAppDispatch();
  // State
  const myUser:IMyUser | null = useAppSelector(selectMyUser);
  const loading = useAppSelector(selectLoading);
  const insuranceCase = useAppSelector((state:RootState) => selectCaseForJsonPatch(state, { caseId: insuranceCaseId }));
  const alert = useAppSelector((state:RootState) => selectPushAlert(state, {
    category: 'case',
    type: 'labels'
  }));

  const [ name, setName ] = useState('');
  const [ color, setColor ] = useState<Colors|string>(Colors.Red);
  const [ stateLabels, setStateLabels ] = useState<ILabel[]>(labels);
  const [ addedLabels, setAddedLabels ] = useState<ILabel[]>([]);
  const [ removedLabels, setRemovedLabels ] = useState<ILabel[]>([]);

  const prevStateLabelsRef = useRef<ILabel[]>([]);

  useEffect(() => {
    prevStateLabelsRef.current = stateLabels;
  }, [stateLabels]);

  const applyLabelsChanges = () => {
    if (!insuranceCase) return;
  
    const caseLabels = insuranceCase.labels || [];
    const prevLabels = prevStateLabelsRef.current;
  
    const removedByOther = stateLabels.filter(
      local => !caseLabels.some(db => db.name === local.name && db.color === local.color)
    );
  
    const addedByOther = caseLabels.filter(
      db => !stateLabels.some(local => local.name === db.name && local.color === db.color)
    );
  
    const baseLabels = caseLabels.filter(
      l => !removedLabels.some(r => r.name === l.name && r.color === l.color)
    );
  
    const baseWithStatuses = baseLabels.map(l => ({
      ...l,
      status: addedByOther.some(a => a.name === l.name && a.color === l.color)
        ? 'addedByOther'
        : undefined
    }));
  
    const combined = [...baseWithStatuses, ...addedLabels.map(l => ({ ...l }))];
  
    const disappearing = removedByOther.map(l => ({ ...l, status: 'removedByOther' }));
  
    const nextLabels: (ILabel & { status?: string })[] = [];

    for (const prev of prevLabels) {
      const stillExists = combined.find(l => l.name === prev.name && l.color === prev.color);
      const removed = disappearing.find(l => l.name === prev.name && l.color === prev.color);
      if (stillExists) {
        nextLabels.push(stillExists);
      } else if (removed) {
        nextLabels.push(removed);
      }
    }
  
    combined.forEach((l) => {
      if (!nextLabels.find(x => x.name === l.name && x.color === l.color)) {
        nextLabels.push(l);
      }
    });

    auditLabelsRef.current = {
      added: [...addedLabels],
      removed: [...removedLabels],
    };
  
    setStateLabels(nextLabels);
    setAddedLabels([]);
    setRemovedLabels([]);
  
    setTimeout(() => {
      setStateLabels(current =>
        current.filter((l:any) => l.status !== 'removedByOther')
      );
    }, 0);
  };

  const handleSave = async () => {
    try {
      const nextLabels = stateLabels.map(label => ({ name: label.name, color: label.color }));
      setStateLabels(nextLabels);
      const nextData = { labels: nextLabels, version }
      await dispatch(patchInsuranceCase({ insuranceCaseId, data: nextData })).unwrap();
      evokeAuditCreation();
      onClose();
    } catch(error){}
  }

  const evokeAuditCreation = () => {
    const { added, removed } = auditLabelsRef.current;
    const allAddedLabels = [...addedLabels, ...added];
    const allRemovedLabels = [...removedLabels, ...removed];

    if ( !allAddedLabels.length && !allRemovedLabels.length ) return;
  
    dispatch(createAuditRecord({
      operation: allAddedLabels.length
        ? AuditOperations.CaseLabelAdding
        : AuditOperations.CaseLabelRemoving
      ,
      data: {
        caseId: insuranceCaseId,
        userName: myUser?.name || '',
        addedLabels: allAddedLabels.map((label:ILabel) => label.name),
        removedLabels: allRemovedLabels.map((label:ILabel) => label.name)
      }
    })).unwrap();
  }

  const handleChange = (event:ChangeEvent<HTMLInputElement>) => {
    setName(event.target.value);
  }

  const handleOnKeyDown = (event:React.KeyboardEvent<HTMLDivElement>) => {
    if ( event.key !== 'Enter' ) return;
    if (name) {
      handleAddLabel();
    } else {
      handleSave();
    }
  }

  const handleAddLabel = () => {
    if ( !name || !color ) return;

    const label = { name, color };

    const foundedIndex = removedLabels.findIndex((l:ILabel) => l.name === label.name);

    if ( foundedIndex !== -1 ){
      setRemovedLabels((prevState:ILabel[]) => prevState.filter((_:any, idx) => idx !== foundedIndex));
    } else {
      setAddedLabels([...addedLabels, label]);
    }

    setStateLabels((prevState:ILabel[]) => [...prevState, label])

    setName('');
    setColor(Colors.Red);
  }

  const handleRemoveLabel = (index:number) => {
    const label = stateLabels[index];

    const foundedIndex = addedLabels.findIndex((l:ILabel) => l.name === label.name);

    if ( foundedIndex !== -1 ){
      setAddedLabels((prevState:ILabel[]) => prevState.filter((_:any, idx) => idx !== foundedIndex));
    } else {
      setRemovedLabels([...removedLabels, label]);
    }

    setStateLabels((prevState:ILabel[]) => prevState.filter((_:any, idx) => idx !== index));
  }

  const handleColorClick = (nextColor:Colors|string) => setColor(nextColor);

  const isDisabled = !!alert && alert.caseId === insuranceCaseId;

  const actions = (
    <Fragment>
      <Button
        name="Cancel case labels dialog"
        onClick={onClose}
      >{t('buttons.close')}</Button>
      <Button
        name="Save case labels dialog"
        onClick={handleSave}
        loading={loading}
        disabled={isDisabled}
        variant="contained"
        color="primary"
      >{t('buttons.save')}</Button>
    </Fragment>
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      title={t('dialogs.caseLabels.title')}
      actions={actions}
      onKeyDown={handleOnKeyDown}
    >
      <CasePushAlert caseId={insuranceCaseId} type="labels" applyFunc={applyLabelsChanges} />
      <Box sx={{ display: 'flex', gap: '8px', mb: '16px' }}>
        <Box flexGrow={1}>
          <Input
            label={t('labels.name')} value={name}
            onChange={handleChange}
            margin="none"
          />
        </Box>
        <Box pt="10px">
          <Button
            name="Add label"
            disabled={!name}
            onClick={handleAddLabel}
            variant="contained"
            color="primary"
          >{t('buttons.add')}</Button>
        </Box>
      </Box>
      <ColorPalette sx={{ mb: 4 }} activeColors={[color]} showNoColorValue={false}>
        {(color) => (
          <ColorPalette.Item
            key={color}
            color={color}
            onClick={handleColorClick}
          />
        )}
      </ColorPalette>
      <Labels
        id={insuranceCaseId}
        labels={stateLabels}
        onRemove={handleRemoveLabel}
      />
    </Dialog>
  )
}

export default CaseLabelsDialog;